import { Button, CircularProgress, DialogActions } from '@mui/material';
import type { DialogMode } from '../hooks/useResponsiveDialog';

interface DialogFormActionsProps {
	mode: DialogMode;
	isLoading: boolean;
	isReadOnly: boolean;
	isMobile: boolean;
	onClose: () => void;
	/**
	 * Custom labels for buttons
	 */
	labels?: {
		cancel?: string;
		close?: string;
		create?: string;
		update?: string;
	};
	/**
	 * Additional buttons to render before the main action buttons
	 */
	extraActions?: React.ReactNode;
}

/**
 * Standardized dialog action buttons for entity dialogs.
 * Handles cancel/close button visibility and submit button state.
 *
 * @example
 * <DialogFormActions
 *   mode={mode}
 *   isLoading={isLoading}
 *   isReadOnly={isReadOnly}
 *   isMobile={isMobile}
 *   onClose={onClose}
 * />
 */
export function DialogFormActions({
	mode,
	isLoading,
	isReadOnly,
	isMobile,
	onClose,
	labels = {},
	extraActions,
}: DialogFormActionsProps) {
	const {
		cancel = 'Hủy',
		close = 'Đóng',
		create = 'Thêm',
		update = 'Cập nhật',
	} = labels;

	const submitLabel = mode === 'create' ? create : update;
	const closeLabel = isReadOnly ? close : cancel;

	return (
		<DialogActions
			sx={{
				flexShrink: 0,
				borderTop: isMobile ? 1 : 0,
				borderColor: 'divider',
				p: isMobile ? 2 : undefined,
			}}
		>
			{/* Cancel/Close button - hidden on mobile (back button in header) */}
			{!isMobile && (
				<Button onClick={onClose} disabled={isLoading}>
					{closeLabel}
				</Button>
			)}

			{/* Extra actions (e.g., save note button) */}
			{extraActions}

			{/* Submit button - hidden in view/read-only mode */}
			{!isReadOnly && (
				<Button
					type="submit"
					variant="contained"
					disabled={isLoading}
					startIcon={isLoading ? <CircularProgress size={20} /> : null}
				>
					{submitLabel}
				</Button>
			)}
		</DialogActions>
	);
}

export default DialogFormActions;
